
import PostDetailCard from "@/components/post/PostDetailCard"
import BusinessLogo from "@/components/business/BusinessLogo"
import FloatingChat from "@/components/chat/FloatingChat"

import postService from "@/services/post.service.js"
import breadcrumb from "@/mixins/breadcrumb"

import { mdiChevronLeft } from "@mdi/js"
import { routeToBusiness } from "@/helpers/routing"

export default {
    name: "Postpage",
    layout: "detailpage",
    components: {
        PostDetailCard,
        BusinessLogo,
        FloatingChat,
        JobList: () => import("@/components/job/JobList"),
    },
    mixins: [breadcrumb],
    async asyncData({ store, route, redirect }) {
        const post = await postService
            .getPost(route.params.postSlug)
            .catch((err) => {
                console.warn(err)
                redirect("/")
            })
        if (post && post.business) {
            await store.dispatch("business/loadJobs", route.params.postSlug)
        }
        return { post }
    },
    meta: {
        hideBottomNav: true,
    },
    data: () => ({
        mdiChevronLeft,
        post: {},
        isChatOpen: false,
    }),
    computed: {
        authorMetaName() {
            const matches = this.post.author.name.match(/(.*) gmbh/i)
            return !!matches && !!matches[1]
                ? matches[1]
                : this.post.author.name
        },
        showFloatingChat() {
            if (this.$can.business() || this.$can.school())
                return false /* Do not show for business & school users */
            if (this.post.author_type != "business")
                return false /* Do not show for region-posts */
            return true
        },
        relatedJobs() {
            return this.post.jobs
        },
        seoContentText() {
            if (this.post.text) {
                const maxLength = 160
                //trim the string to the maximum length
                if (this.post.text.length > maxLength) {
                    var trimmedString = this.post.text.substr(0, maxLength)

                    //re-trim if we are in the middle of a word and
                    return (
                        trimmedString.substr(
                            0,
                            Math.min(
                                trimmedString.length,
                                trimmedString.lastIndexOf(" ")
                            )
                        ) + "..."
                    )
                } else {
                    return this.post.text
                }
            } else {
                return (
                    "Erfahre wie die Ausbildung bei " +
                    this.post.author.name +
                    " wirklich abläuft und finde einzigartige Berufseinblicke"
                )
            }
        },
        logoJpg() {
            let logo = null
            if (this.post.author_type == "business") {
                logo = this.$getImage({
                    image: this.post.author.logo,
                    preferedSize: "3",
                }).src.replace("webp", "jpg")
            }
            return logo
        },
        ogImage() {
            if (this.post.video) {
                return this.post.video.thumbnail_url
            } else {
                return this.$getOGImage({
                    image: this.post.image,
                }).src
            }
        },
        ogImageType() {
            if (this.post.video) {
                return "image/jpeg"
            } else {
                return this.$getOGImage({
                    image: this.post.image,
                }).type
            }
        },
        routeToBusiness() {
            return routeToBusiness(this.post.author)
        },
    },
    methods: {
        share() {
            this.$refs.detailCard.share()
        },
        getStructuredData() {
            let result = [
                {
                    "@context": "http://schema.org/",
                    "@type": "NewsArticle",
                    mainEntityOfPage: {
                        "@type": "WebPage",
                        "@id": this.$config.baseURL + this.$route.fullPath,
                    },
                    author: {
                        "@type": "Organization",
                        name: this.post.author.name,
                        logo: this.logoJpg,
                    },
                    headline: this.post.title,
                    image: [this.ogImage],
                    datePublished: this.post.published_at,
                    dateModified: this.post.updated_at,
                },
            ]
            if (this.post.author.primary_address) {
                result[0].author.address = {
                    "@type": "PostalAddress",
                    addressCountry:
                        this.post.author.primary_address.country_code,
                    postalCode: this.post.author.primary_address.postal_code,
                    streetAddress: this.post.author.primary_address.street,
                    addressLocality: this.post.author.primary_address.city,
                    addressRegion: this.post.author.primary_address.state,
                }
            }
            return result
        },
    },
    head() {
        return {
            title: this.authorMetaName + ": " + this.post.title,
            meta: [
                {
                    hid: "description",
                    name: "description",
                    content: this.seoContentText,
                },
                {
                    hid: "og:title",
                    name: "og:title",
                    content: this.authorMetaName + ": " + this.post.title,
                },
                {
                    hid: "og:description",
                    name: "og:description",
                    content: this.seoContentText,
                },
                {
                    hid: "og:image",
                    name: "og:image",
                    content: this.ogImage,
                },
                {
                    hid: "og:image:type",
                    name: "og:image:type",
                    content: this.ogImageType,
                },
            ],
            __dangerouslyDisableSanitizers: ["script"],
            script: [
                {
                    innerHTML: JSON.stringify(this.getStructuredData()),
                    type: "application/ld+json",
                },
                {
                    innerHTML: JSON.stringify(
                        this.getBreadcrumb([
                            { identifier: "post", post: this.post },
                        ])
                    ),
                    type: "application/ld+json",
                },
            ],
        }
    },
}
